<template>
  <div class="flex flex-col gap-4 root-settings">
    <NForm requireMarkPlacement="right" :showFeedback="false" size="medium">
      <NGrid :cols="1" :yGap="16">
        <NFormItemGridItem :label="labels.name" v-bind="bindValidation('name')" required>
          <CInput v-model:value="form.name" :disabled="isCanvasLocked" :placeholder="placeholders.name" />
        </NFormItemGridItem>

        <NFormItemGridItem
          v-if="isCRM && !activeCanvas?.is_template && !activeCanvas?.is_section"
          v-bind="bindValidation('account')"
          :label="labels.account"
        >
          <CSelect
            bordered
            clearable
            :disabled="!launchDarkly.copy_context_to_canvas || isCanvasLocked"
            filterable
            :loading="accountsLoading"
            :options="accounts"
            :placeholder="placeholders.account"
            remote
            :value="form.account"
            @blur="updateAccountSearch('')"
            @search="updateAccountSearch($event)"
            @update:show="updateAccountSearch('')"
            @update:value="setAccount"
          />
        </NFormItemGridItem>

        <NFormItemGridItem
          v-if="!activeCanvas?.is_template && !activeCanvas?.is_section && activeCanvas?.template?.name"
          :label="labels.parentTemplate"
        >
          <CSelect
            v-model:value="form.parentTemplate"
            :bordered="true"
            disabled
            :fallback-option="
              () => ({ label: activeCanvas?.template?.name ?? form.parentTemplate, value: form.parentTemplate })
            "
            :options="[{ label: activeCanvas?.template?.name, value: activeCanvas?.template?.id }]"
          />
        </NFormItemGridItem>

        <NFormItemGridItem :label="labels.status" v-bind="bindValidation('status')" required>
          <CSelect v-model:value="form.status" bordered :disabled="isCanvasLocked" :options="statusOptions" required />
        </NFormItemGridItem>

        <NFormItemGridItem v-if="shouldShowThumbnail" :label="labels.thumbnailUrl">
          <CInput
            v-model:value="form.thumbnailUrl"
            :disabled="isCanvasLocked"
            :placeholder="placeholders.thumbnailUrl"
          />
        </NFormItemGridItem>

        <NFormItemGridItem :label="labels.tags">
          <CTags v-model:value="form.tags" :disabled="isCanvasLocked" :suggested-tags-fetcher="fetchTags" />
        </NFormItemGridItem>
      </NGrid>
    </NForm>

    <div v-if="hasConfigEditor" class="mb-15 root-settings__metadata">
      <div class="root-settings__metadata__title">
        {{ t('canvasUI.canvasBuilder.canvasSettings.form.metadataTitle') }}
      </div>
      <CConfigEditor
        ref="configEditorRef"
        :initialValues="activeCanvas?.metadata"
        :loading="isSavingCanvas"
        :read-only="isConfigEditorReadOnly"
        :template="instanceMetadataTemplate as ConfigForm"
      />
    </div>

    <div
      class="absolute bottom-0 left-0 z-2 mx-8 py-4"
      :style="{
        width: 'calc(100% - 2 * var(--n-body-padding))',
        backgroundColor: themeVars.base,
      }"
    >
      <CButton block :disabled="!!v.$errors.length" :loading="isSavingCanvas" type="primary" @click="submit()">
        {{ t('canvasUI.canvasBuilder.canvasSettings.form.save') }}
      </CButton>
    </div>
  </div>
</template>

<script setup lang="ts">
import { reactive, ref, onMounted, computed, inject, type Ref, type ComputedRef } from 'vue'
import { NForm, NFormItemGridItem, NGrid } from 'naive-ui'
import { useI18n } from 'vue-i18n'
import { isString } from 'lodash-es'
import { useVuelidate } from '@vuelidate/core'
import { required } from '@vuelidate/validators'

import CButton from '@lib/components/CButton/CButton.vue'
import CInput from '@lib/components/CInput/CInput.vue'
import CSelect from '@lib/components/CSelect/CSelect.vue'
import CTags from '@lib/components/CTags/CTags.vue'
import { CanvasStatus } from '@lib/types/canvases'
import type { AccountRequest, CanvasRetrieve, PatchedCanvasUpdateRequest, MetadataTemplate } from '@openapi'
import { CanvasBuilderMode, type CanvasBuilderProps } from '@canvas-builder/types/canvas'
import useCanvas from '@canvas-builder/composables/useCanvas'
import useBindValidation from '@lib/composables/useBindValidation'
import useToast from '@lib/composables/useToast'
import CConfigEditor from '@lib/components/CConfigEditor/CConfigEditor.vue'
import useThemeVars from '@lib/composables/useThemeVars'
import useSuggestedTags from '@lib/composables/suggestedTags.use'
import type { ConfigForm } from '@lib/components/CConfigEditor/CConfigEditor.types'
import type { LaunchDarklyEnv } from '@lib/types/launchDarkly.types'
import type { InstanceSettings } from '@lib/types/instanceSettings.types'
import type { OrganizationSettings } from '@lib/types/organizationSettings.types'
import useCanvasLocks from '@lib/composables/useCanvasLocks'
import { toCssClassKey } from '@lib/util/toCssClassKey'

interface Form {
  name: CanvasRetrieve['name']
  account: AccountRequest['id'] | null
  status: CanvasStatus
  parentTemplate: CanvasRetrieve['id']
  tags: CanvasRetrieve['tags']
  isEditable: boolean
  thumbnailUrl: CanvasRetrieve['thumbnail_url']
}

const { t } = useI18n()
const themeVars = useThemeVars()
const { toast } = useToast()

const launchDarkly = inject<ComputedRef<LaunchDarklyEnv>>(
  'launchDarkly',
  computed(() => ({})),
)
const pitcherSettings = inject<ComputedRef<OrganizationSettings & InstanceSettings>>(
  'pitcherSettings',
  computed(() => ({})),
)

const TAGS_LIMIT = 5
const { activeCanvas, onSfdcAccountSearch, save, mode, isCRM } = useCanvas()
const { fetchSuggestedInstanceTags } = useSuggestedTags()
const { isCanvasLocked: getIsCanvasLocked } = useCanvasLocks()
const isCanvasLocked = computed(() => !!activeCanvas.value?.id && getIsCanvasLocked(activeCanvas.value.id))
const labels: Record<keyof Form, string> = {
  name: t('canvasUI.canvasBuilder.canvasSettings.form.label.name'),
  account: t('canvasUI.canvasBuilder.canvasSettings.form.label.account'),
  status: t('canvasUI.canvasBuilder.canvasSettings.form.label.status'),
  parentTemplate: t('canvasUI.canvasBuilder.canvasSettings.form.label.template'),
  tags: t('canvasUI.canvasBuilder.canvasSettings.form.label.tags'),
  isEditable: t('canvasUI.canvasBuilder.canvasSettings.form.label.editable'),
  thumbnailUrl: t('canvasUI.canvasBuilder.canvasSettings.form.label.thumbnailUrl'),
}
const placeholders: Partial<Record<keyof Form, string>> = {
  name: t('canvasUI.canvasBuilder.canvasSettings.form.placeholder.add', { field: labels.name }),
  account: t('canvasUI.canvasBuilder.canvasSettings.form.placeholder.select', { field: labels.account }),
  tags: t('canvasUI.canvasBuilder.canvasSettings.form.placeholder.addMax', {
    field: labels.tags?.toLowerCase(),
    value: TAGS_LIMIT,
  }),
  thumbnailUrl: t('canvasUI.canvasBuilder.canvasSettings.form.placeholder.thumbnailUrl'),
}

const form: Form = reactive({
  name: activeCanvas.value!.name,
  account: isCRM && activeCanvas.value?.account?.id ? activeCanvas.value.account.id : null,
  status: activeCanvas.value?.is_draft ? CanvasStatus.DRAFT : CanvasStatus.READY,
  parentTemplate: activeCanvas.value!.template?.id ?? '',
  tags: activeCanvas.value!.tags ?? [],
  isEditable: activeCanvas.value?.is_editable ?? false,
  thumbnailUrl: activeCanvas.value?.thumbnail_url ?? activeCanvas.value?.metadata?.thumbnail,
})

const statusOptions = [
  {
    label: t('canvasUI.canvasBuilder.canvasSettings.form.options.status.draft'),
    value: CanvasStatus.DRAFT,
  },
  {
    label: t('canvasUI.canvasBuilder.canvasSettings.form.options.status.ready'),
    value: CanvasStatus.READY,
  },
]

const v = useVuelidate(
  {
    name: {
      required,
    },
    status: {
      required,
    },
  },
  form,
  { $autoDirty: true },
)

const requiredMsg = (field: keyof typeof labels) =>
  t('canvasUI.canvasBuilder.canvasSettings.form.validation.required', { field: labels[field] })

const { bindValidation } = useBindValidation<Form>(v, {
  name: { required: { msg: requiredMsg('name') } },
  status: { required: { msg: requiredMsg('status') } },
})

const isSavingCanvas = ref(false)
const selectedAccount = ref<null | { id: string; name: string }>(null)
const accountSearchTerm = ref('')
const instanceMetadataTemplate = ref<null | undefined | MetadataTemplate>(null)
const hasConfigEditor = computed(
  () =>
    (mode.value === CanvasBuilderMode.ADMIN ||
      (mode.value === CanvasBuilderMode.IMPACT &&
        !activeCanvas.value?.is_section &&
        !activeCanvas.value?.is_template)) &&
    !activeCanvas.value?.is_block &&
    activeCanvas.value &&
    instanceMetadataTemplate.value,
)

const isConfigEditorReadOnly = computed(
  () =>
    mode.value === CanvasBuilderMode.IMPACT &&
    (activeCanvas.value?.is_section || !launchDarkly.value.allow_rep_canvas_metadata_edit),
)
const configEditorRef = ref<null | InstanceType<typeof CConfigEditor>>(null)
const fetchInstanceMetadataTemplate = inject<CanvasBuilderProps['fetchInstanceMetadataTemplate']>(
  'fetchInstanceMetadataTemplate',
)

const shouldShowThumbnail = computed(
  () =>
    (activeCanvas.value?.is_section && !activeCanvas.value?.is_template) ||
    (mode.value === CanvasBuilderMode.ADMIN && !!pitcherSettings.value?.enable_thumbnails_for_templates_and_blocks),
)

let accountsLoading: Ref<boolean> = ref(false)
let accounts: Ref<{ label: string; value: string }[]> = ref([])

const sfdcAccountSearchResult = onSfdcAccountSearch.value(accountSearchTerm)

if (sfdcAccountSearchResult) {
  // eslint-disable-next-line vue/no-ref-as-operand
  accountsLoading = sfdcAccountSearchResult.accountsLoading
  // eslint-disable-next-line vue/no-ref-as-operand
  accounts = sfdcAccountSearchResult.accounts
}

if (isCRM && activeCanvas.value?.account?.id) {
  accounts.value = [
    { value: activeCanvas.value.account.id, label: activeCanvas.value.account.name ?? activeCanvas.value.account.id },
  ]
}

const updateAccountSearch = (value: string) => {
  accountSearchTerm.value = value
}

const setAccount = (value: string | null) => {
  form.account = value

  if (isString(value)) {
    const account = accounts.value.find((a) => a.value === value)

    if (account) {
      selectedAccount.value = {
        id: value,
        name: account.label,
      }
      return
    }
  }

  selectedAccount.value = null
}

const fetchTags = (search: string) => {
  const isSectiontemplate = activeCanvas.value?.is_section && activeCanvas.value?.is_template

  if (isSectiontemplate) return []

  const canvasType = activeCanvas.value?.is_section
    ? 'section'
    : activeCanvas.value?.is_template
      ? 'template'
      : activeCanvas.value?.is_block
        ? 'block'
        : 'canvas'

  return fetchSuggestedInstanceTags(search, {
    type: ['canvases'],
    canvasType,
  })
}

const parseForm = (form: Form): PatchedCanvasUpdateRequest => {
  const thumbnailUrl = form.thumbnailUrl?.trim()

  const base = {
    name: form.name,
    is_draft: CanvasStatus.DRAFT === form.status,
    is_editable: form.isEditable,
    tags: form.tags,
    thumbnail_url: thumbnailUrl === '' ? null : thumbnailUrl,
  }

  const withAccount = {
    ...base,
    account: (() => {
      const id = selectedAccount.value?.id
      if (!id) return null
      if (pitcherSettings.value?.enable_crm_privacy_mode) {
        const privacyModeAccount: AccountRequest = { id }
        return privacyModeAccount
      }
      const fullAccount: AccountRequest = { id, name: selectedAccount.value?.name }
      return fullAccount
    })(),
  }

  return !activeCanvas.value?.is_template && !activeCanvas.value?.is_section ? withAccount : base
}

const submit = async () => {
  try {
    let metadata
    if (hasConfigEditor.value && configEditorRef.value && !isConfigEditorReadOnly.value) {
      const { success, config, firstErrorFieldName } = await configEditorRef.value.validate()
      if (!success) {
        scrollToFirstValidationError(firstErrorFieldName)
        return
      }
      metadata = config
    }

    const data = parseForm(form)
    isSavingCanvas.value = true
    await save(activeCanvas.value!.id, { ...data, ...(metadata && { metadata }) }, false)
    toast.success(t('canvasUI.canvasBuilder.canvasSettings.form.settingsSaved'))
  } catch (error) {
    console.error(error)
  } finally {
    isSavingCanvas.value = false
  }
}

function scrollToFirstValidationError(firstErrorFieldName?: string) {
  if (!firstErrorFieldName) return
  const rootEl = document.querySelector('.root-settings') as HTMLElement | null
  const target = rootEl?.querySelector(
    `.config-field.config-field--${toCssClassKey(firstErrorFieldName)}`,
  ) as HTMLElement | null
  if (target?.scrollIntoView) target.scrollIntoView({ behavior: 'smooth', block: 'center' })
}

onMounted(async () => {
  if (!fetchInstanceMetadataTemplate) return

  try {
    instanceMetadataTemplate.value = await fetchInstanceMetadataTemplate()
  } catch (error) {
    console.warn(`Failed to fetch instance metadata template: `, error)
  }
})

defineExpose({
  isBusy: isSavingCanvas,
})
</script>

<style lang="scss" scoped>
.root-settings__metadata__title {
  font-size: 20px;
  line-height: 32px;
  letter-spacing: 0.25px;
}
</style>
